/**
 * StoreLinkBlock — v149.69, v149.70 ("Shop Now").
 *
 * The Storefront block shown as a Shop Now button that opens the store on
 * its own page (`/store/{slug}`) instead of listing products inside the
 * Web Link page — so the page stays short and fast however large the
 * catalog is. Chosen per block: `config.display === "link"` (the default,
 * "grid", is the product grid the block has always been).
 *
 * The button is the theme-safe CTA the form blocks use
 * (`ah-collapsible-cta`: solid, readable in every theme). The store page's
 * layout and checkout are the seller's Storefront settings.
 */
import { Link } from "react-router-dom";
import { ShoppingBag, ArrowRight } from "lucide-react";
import ProductThumb from "../commerce/ProductThumb";

export default function StoreLinkBlock({ block, slug, products = [], onNavigate }) {
  const c = block.config || {};
  const href = slug ? `/store/${slug}` : "#";
  // Up to three product photos (or initials) as a small preview.
  const peek = products.slice(0, 3);
  return (
    <div className="ah-block" data-testid={`block-storefront-link-${block.block_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        {peek.length > 0 ? (
          <div className="flex flex-shrink-0 -space-x-3" aria-hidden="true">
            {peek.map((p) => (
              <ProductThumb
                key={p.product_id}
                src={(p.image_urls && p.image_urls[0]) || p.image_url}
                name={p.title}
                width={160}
                className="w-11 h-11 border-2 border-current rounded-full bg-white"
              />
            ))}
          </div>
        ) : (
          <span className="w-11 h-11 flex-shrink-0 border-2 border-current rounded-full inline-flex items-center justify-center">
            <ShoppingBag className="w-5 h-5" strokeWidth={2.5} />
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-base leading-tight break-words">{c.heading || "Shop"}</p>
          <p className="text-xs ah-muted leading-snug mt-0.5 line-clamp-2">
            {c.link_text || "Browse all our products"}
          </p>
        </div>
      </div>
      <Link
        to={href}
        onClick={(e) => {
          if (!slug) e.preventDefault();
          onNavigate?.(e, href);
        }}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-current px-4 py-3 font-black text-base uppercase tracking-wider transition-all hover:opacity-90 ah-collapsible-cta"
        style={{
          background: "var(--ah-btn-bg, var(--ah-block-fg, #1a1a1a))",
          color: "var(--ah-btn-fg, var(--ah-block-bg, #ffffff))",
          boxShadow: "3px 3px 0 var(--ah-block-fg, currentColor)",
        }}
        data-testid={`block-storefront-link-cta-${block.block_id}`}
      >
        <ShoppingBag className="w-4 h-4" strokeWidth={2.5} />
        {c.button_label || "Shop Now"}
        <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
      </Link>
    </div>
  );
}
